import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { cadsRoleButtonKeydown, cadsRoleButtonKeyup } from '../../a11y/cads-role-button-keyboard';
import { CadsMenuRowConfig } from './cads-menu-row.config';

/**
 * **Container only:** horizontal flex shell for **custom** menu line content projected via `ng-content`. Padding and gap
 * match menu spacing; no icon/title/trailing API—compose bits and controls as needed. Use {@link CadsMenuRowItemComponent}
 * for the pre-built icon + heading + trailing pattern. When {@link CadsMenuRowConfig.tappable} is true, the row behaves
 * like a single button; nested interactive widgets should call `stopPropagation()` if they must not trigger {@link CadsMenuRowComponent.tapped}.
 *
 * Optional flex **spacer**: add an element with class `cads-menu-row__spacer` between leading and trailing groups so the
 * trailing cluster pins to the end (styles pierce projected content via `::ng-deep`).
 */
@Component({
  selector: 'cads-menu-row',
  standalone: true,
  templateUrl: './cads-menu-row.component.html',
  styleUrl: './cads-menu-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CadsMenuRowComponent {
  /** Row options; default not tappable. */
  readonly config = input<CadsMenuRowConfig>({});

  /** Emitted when {@link CadsMenuRowConfig.tappable} is true and the user activates the row (click, Enter on keydown, or Space on keyup). */
  readonly tapped = output<void>();

  readonly _isTappable = computed(() => this.config().tappable ?? false);

  protected _onActivate(): void {
    if (!this._isTappable()) {
      return;
    }
    this.tapped.emit();
  }

  protected _onKeydown(event: KeyboardEvent): void {
    cadsRoleButtonKeydown(event, this._isTappable(), () => this._onActivate());
  }

  protected _onKeyup(event: KeyboardEvent): void {
    cadsRoleButtonKeyup(event, this._isTappable(), () => this._onActivate());
  }
}
